export const collectedDetailsToPostingDetails = (details = {}) => ({
  companyName: details.company_name || details.companyName || '',
  employmentType: details.employment_type || details.employmentType || '',
  workplaceType: details.workplace_type || details.workplaceType || '',
  jobLocation: details.location || details.job_location || details.jobLocation || '',
  salaryMin: details.salary_min ?? details.salaryMin ?? '',
  salaryMax: details.salary_max ?? details.salaryMax ?? '',
  recruiterName: details.recruiter_name || details.recruiterName || '',
  recruiterNumber: details.recruiter_number || details.recruiterNumber || '',
  allowCandidatesToCall: details.allow_call ?? details.allowCandidatesToCall ?? null,
  experienceMin: details.experience_min ?? details.experienceMin ?? '',
  experienceMax: details.experience_max ?? details.experienceMax ?? '',
  department: details.department || '',
});

export const hasMeaningfulJobPostingValue = (value, { allowZero = false } = {}) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return allowZero ? true : value > 0;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const firstMeaningfulJobPostingValue = (values, { allowZero = false, fallback = '' } = {}) => {
  for (const value of values) {
    if (hasMeaningfulJobPostingValue(value, { allowZero })) return value;
  }
  return fallback;
};

export const normalizeJobPostingFieldKey = (fieldName) => {
  const mapping = {
    companyName: 'company_name',
    salaryMin: 'salaryMin',
    salary_max: 'salaryMax',
    salaryMax: 'salaryMax',
    salary_min: 'salaryMin',
    experienceMin: 'experienceMin',
    experience_min: 'experienceMin',
    experienceMax: 'experienceMax',
    experience_max: 'experienceMax',
    allowCandidatesToCall: 'allow_call',
    recruiterName: 'recruiter_name',
    recruiterNumber: 'recruiter_number',
  };
  return mapping[fieldName] || fieldName;
};

export const buildJobPostingCollectedDetails = ({
  collectedDetails = {},
  postingValues = {},
  preparedValues = {},
  channels = [],
  finalRequirements = {},
} = {}) => ({
  company_name: firstMeaningfulJobPostingValue([
    preparedValues.company_name,
    collectedDetails.company_name,
    postingValues.companyName,
    postingValues.company_name,
  ]),
  employment_type: firstMeaningfulJobPostingValue([
    preparedValues.employment_type,
    collectedDetails.employment_type,
    postingValues.employmentType,
    postingValues.employment_type,
  ]),
  workplace_type: firstMeaningfulJobPostingValue([
    preparedValues.workplace_type,
    collectedDetails.workplace_type,
    postingValues.workplaceType,
    postingValues.workplace_type,
  ]),
  location: firstMeaningfulJobPostingValue([
    preparedValues.location,
    collectedDetails.location,
    postingValues.jobLocation,
    postingValues.job_location,
    finalRequirements?.location,
  ]),
  salary_min: firstMeaningfulJobPostingValue([
    preparedValues.salary_min,
    collectedDetails.salary_min,
    postingValues.salaryMin,
    postingValues.salary_min,
  ], { fallback: '' }),
  salary_max: firstMeaningfulJobPostingValue([
    preparedValues.salary_max,
    collectedDetails.salary_max,
    postingValues.salaryMax,
    postingValues.salary_max,
  ], { fallback: '' }),
  allow_call: [
    preparedValues.allow_call,
    preparedValues.allowCandidatesToCall,
    collectedDetails.allow_call,
    collectedDetails.allowCandidatesToCall,
    postingValues.allowCandidatesToCall,
    postingValues.allow_call,
  ].find((value) => value !== undefined && value !== null) ?? null,
  recruiter_name: firstMeaningfulJobPostingValue([
    preparedValues.recruiter_name,
    collectedDetails.recruiter_name,
    postingValues.recruiterName,
    postingValues.recruiter_name,
  ]),
  recruiter_number: firstMeaningfulJobPostingValue([
    preparedValues.recruiter_number,
    collectedDetails.recruiter_number,
    postingValues.recruiterNumber,
    postingValues.recruiter_number,
  ]),
  department: firstMeaningfulJobPostingValue([
    preparedValues.department,
    collectedDetails.department,
    postingValues.department,
  ]),
  job_title: firstMeaningfulJobPostingValue([
    preparedValues.job_title,
    preparedValues.title,
    collectedDetails.job_title,
    postingValues.jobTitle,
    postingValues.job_title,
    finalRequirements?.title,
    finalRequirements?.role,
  ]),
  experience_min: firstMeaningfulJobPostingValue([
    preparedValues.experience_min,
    collectedDetails.experience_min,
    postingValues.experienceMin,
    postingValues.experience_min,
  ], { allowZero: true, fallback: '' }),
  experience_max: firstMeaningfulJobPostingValue([
    preparedValues.experience_max,
    collectedDetails.experience_max,
    postingValues.experienceMax,
    postingValues.experience_max,
  ], { allowZero: true, fallback: '' }),
  _channels: channels,
  _prepared_job_details: preparedValues._prepared_job_details || collectedDetails._prepared_job_details || null,
  _platforms: preparedValues._platforms || collectedDetails._platforms || channels,
});

export const getMissingJobPostingFields = (details = {}) => {
  const missingFields = [];
  const salaryMin = Number(details.salary_min);
  const salaryMax = Number(details.salary_max);
  const experienceMin = details.experience_min;
  const experienceMax = details.experience_max;

  if (!hasMeaningfulJobPostingValue(details.company_name)) missingFields.push('company_name');
  if (!hasMeaningfulJobPostingValue(details.salary_min) || Number.isNaN(salaryMin) || salaryMin <= 0) {
    missingFields.push('salaryMin');
  }
  if (!hasMeaningfulJobPostingValue(details.salary_max) || Number.isNaN(salaryMax) || salaryMax <= 0) {
    missingFields.push('salaryMax');
  }
  if (!hasMeaningfulJobPostingValue(experienceMin, { allowZero: true })) missingFields.push('experienceMin');
  if (!hasMeaningfulJobPostingValue(experienceMax, { allowZero: true })) {
    missingFields.push('experienceMax');
  } else if (
    hasMeaningfulJobPostingValue(experienceMin, { allowZero: true }) &&
    Number(experienceMax) < Number(experienceMin)
  ) {
    missingFields.push('experienceMax');
  }
  if (details.allow_call === null || details.allow_call === undefined) missingFields.push('allow_call');
  if (details.allow_call === true) {
    if (!hasMeaningfulJobPostingValue(details.recruiter_name)) missingFields.push('recruiter_name');
    if (!hasMeaningfulJobPostingValue(details.recruiter_number)) missingFields.push('recruiter_number');
  }

  return [...new Set(missingFields)];
};
